import isEmpty from 'lodash/isEmpty';
import { TFunction } from 'react-i18next';

import {
  AffinityAggregateEvent,
  AffinitySystemEvent,
  AggregateEventOrder,
  BrandAffinityXAI,
  CampaignAction,
  caseInsensitiveComparator,
  EngagementData,
} from 'modules/ai/XAI/audienceUserProfile';
import { BrandAffinityLevel, getScoreDataByLevel } from 'modules/domains/brandAffinity';
import { MessageMedium } from 'constants/messageMediums';
import { getLabelByMedium } from 'utils/messageMediums';

import { ensureEnum } from 'utils/tsHelpers';

import { SystemEventName } from '../../../../domain/event';
import { formatToDisplayDate, constructEngagementEvent, getRelativeToMonthSpan } from './display';

export const transformScoreHistory = (history: BrandAffinityXAI['history']) => {
  return history.map((record) => ({
    ...record,
    date: formatToDisplayDate(record.date),
    relativeDate: getRelativeToMonthSpan(record.date ?? ''),
  }));
};

export const ensureSystemEvent = (channel: string, event: string) => {
  const input = `${channel}${event}`.toLowerCase();
  return ensureEnum(input, SystemEventName, SystemEventName.emailOpen, caseInsensitiveComparator);
};

export const formatActionWithMedium = (action: CampaignAction, channelMedium: MessageMedium) => {
  const actionByChannel = {
    [CampaignAction.Send]: 'sent',
    [CampaignAction.Open]: 'opened',
    [CampaignAction.Click]: 'clicked',
  };
  const actionDisplay = actionByChannel[action] ?? actionByChannel[CampaignAction.Send];
  return `${getLabelByMedium(channelMedium)} ${actionDisplay} `;
};

export const defaultEngagementStats: Array<{
  isDefault: boolean;
  event: AffinityAggregateEvent['event'] | string;
  plural: string;
}> = [
  { isDefault: true, event: AffinitySystemEvent.Send, plural: 'sends' },
  { isDefault: true, event: AffinitySystemEvent.Open, plural: 'opens' },
  { isDefault: true, event: AffinitySystemEvent.Click, plural: 'clicks' },
];
export const transformEngagementData = (
  isError: boolean,
  engagement: AffinityAggregateEvent | { isDefault: boolean; event: string },
) => {
  if ('isDefault' in engagement || isError) {
    return {
      count: 0,
      event: engagement.event,
      eventType: constructEngagementEvent(engagement.event),
      isDefault: true,
      labelCompare: BrandAffinityLevel.UNSCORED,
      median: 0,
    };
  }
  return {
    count: engagement.count,
    event: engagement.event,
    eventType: constructEngagementEvent(engagement.event),
    labelCompare: engagement.compareFrom,
    median: engagement.medianAbsolute,
  };
};

export const getCombinedEngagementData = (
  isError: boolean,
  aggregateData: Array<AffinityAggregateEvent> | undefined,
): EngagementData[] => {
  if (isEmpty(aggregateData) || isError) {
    return defaultEngagementStats.map((data) => transformEngagementData(isError, data));
  }
  return defaultEngagementStats
    .map((defaultStat) => {
      const engagementStat =
        aggregateData?.find(
          (data) => data.event === defaultStat.event || data.event === defaultStat.plural,
        ) || defaultStat;
      return transformEngagementData(false, engagementStat);
    })
    .sort(
      (eventA, eventB) => AggregateEventOrder[eventA.event] - AggregateEventOrder[eventB.event],
    );
};

export const getComparisonLabel = (
  t: TFunction,
  label: AffinityAggregateEvent['compareFrom'],
): AffinityAggregateEvent['compareFrom'] => {
  let affinityLabel = label;
  if (label !== 'all') {
    affinityLabel = t(getScoreDataByLevel(label).brandText);
  }
  return affinityLabel;
};

export const getComparisonText = (count: number, median: number) => {
  let medianCompare = 'Equal to';
  if (count > median) {
    medianCompare = 'Higher than';
  }
  if (count < median) {
    medianCompare = 'Lower than';
  }
  return medianCompare;
};
